"use client";

import "./[locale]/globals.css";

import Script from "next/script";
import { NextIntlClientProvider } from "next-intl";

import LocationMapCard from "@/app/[locale]/contact-us/components/LocationMapCard";

// Hardcoded EN messages — next-intl requires a provider even in global-error
// which renders outside the normal app tree (no locale layout / providers).
const contactMessages = {
  showroom: {
    weekdays: "Sun-Sat : 10 AM – 10 PM",
    email: "info@albacars.ae",
    callUs: "Call Us",
    phone: "Phone",
    workingHours: "Working hours",
    emailLabel: "Email",
    getDirections: "Get directions",
  },
};

export default function GlobalError({ reset }: { error: Error & { digest?: string }; reset: () => void }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Alba Cars — We&apos;ll Be Back Soon</title>
        <Script src="https://elfsightcdn.com/platform.js" strategy="afterInteractive" />
      </head>
      <body
        className="bg-secondary"
        style={{
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <NextIntlClientProvider locale="en" messages={{ Contact: contactMessages }}>
          <div className="flex min-h-screen items-start justify-center gap-10 px-6 py-14 lg:items-center xl:px-20">
            {/* ── Left column ── */}
            <div className="flex w-full max-w-xl flex-col items-center text-center lg:items-start lg:text-left">
              {/* Logo */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/assets/images/albacars-logo.png" alt="Alba Cars" className="mb-10 h-10 object-contain" />

              {/* Heading */}
              <h1
                className="mb-3 text-3xl font-bold text-primary-foreground"
                style={{
                  fontFamily: "'Urbanist', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                We&apos;ll Be Back Soon
              </h1>

              {/* Message */}
              <p className="mb-2 max-w-md text-base leading-relaxed text-secondary-foreground">
                Our website is currently down due to disruptions that affected{" "}
                <span className="font-semibold text-primary-foreground">Amazon AWS data centers</span> in the region.
              </p>
              <p className="mb-10 max-w-md text-base leading-relaxed text-secondary-foreground">
                We are still open — please contact us or visit our showroom.
              </p>

              {/* Location map card */}
              <div className="w-full text-left">
                <LocationMapCard
                  location={{
                    title: "Visit Our Showroom",
                    address: {
                      line1: "Al Asayel St - Al Quoz",
                      line2: "Al Quoz Industrial Area 1, Dubai",
                    },
                  }}
                  showContactInfo
                  mapClassName="h-64"
                />
              </div>

              {/* Retry */}
              <button
                onClick={() => reset()}
                className="mt-6 rounded-md border border-grey-border/30 px-6 py-2.5 text-sm text-secondary-foreground transition-colors hover:border-grey-border hover:text-primary-foreground"
              >
                Try again
              </button>
            </div>

            {/* ── Right column — Instagram feed ── */}
            <div className="hidden w-full max-w-sm shrink-0 lg:block xl:max-w-md">
              <p
                className="mb-4 text-lg font-semibold text-primary-foreground"
                style={{
                  fontFamily: "'Urbanist', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                In the meantime, check out our car stock
              </p>
              <div className="elfsight-app-c4bb04f5-5de8-48bf-84b0-dfc07a1feb77" data-elfsight-app-lazy />
            </div>
          </div>
        </NextIntlClientProvider>
      </body>
    </html>
  );
}
